/**
 * Main Layout Component
 *
 * @module shared/layouts/MainLayout
 *
 * Primary application layout with sidebar navigation.
 * Used for all authenticated pages.
 *
 * Responsive Behavior:
 * - Desktop (≥ 960px): Sidebar always visible, main content margin adjusts
 * - Mobile (< 960px): Sidebar overlays content with backdrop, no margin shift
 */

import Box from '@mui/material/Box';
import { useQueryClient } from '@tanstack/react-query';
import { Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useMediaQuery, useTheme } from '@mui/material';

import { PageLoading } from '@/shared/components';
import { SidebarMain, SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH } from '@/shared/components/navigation';
import { useTelephonyHotkeys } from '@/shared/hooks';
import { useSocketIOContext } from '@/shared/services/socketio';
import { SocketIONamespace } from '@/shared/services/socketio/types';
import { useAppStore } from '@/shared/store';
import { useExtensionType, useLogExtensionType } from '@/modules/auth';
import { useWorkTime, useWorkTimeType } from '@/modules/workTime';
import { callFollowUpKeys } from '@/modules/call-follow-up/application/hooks';
import { callHistoryKeys } from '@/modules/call-history/application/hooks/useCallHistory';
import { CallOverlayBar, CallOverlayModal, PhoneCallSummaryModal, ProcessingTranscriptModal } from '@/modules/queue/presentation/components';
import { v2CallResponseToQueueModel } from '@/modules/queue/application/mappers/v2CallResponseToQueueModel';
import { useCallOverlayQueue, useQueueHoldControl } from '@/modules/queue/application/hooks';
import { useSipCallStore } from '@/modules/webrtc';

import type { QueueModel, CallStatus } from '@/modules/queue/domain/types';
import type { V2CallResponse, CallStatusEventData, CallSummaryCreatedEventData } from '@/shared/services/socketio/types';

const ENABLE_CALL_OVERLAY = true;

type CallPhase = 'idle' | 'incoming' | 'ringing' | 'connected';

const CALL_ANNOUNCEMENTS: Record<Exclude<CallPhase, 'idle'>, string> = {
  incoming: 'Incoming call',
  ringing: 'Call ringing',
  connected: 'Call connected',
};

const visuallyHiddenSx = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

// ============================================================================
// Main Layout Component
// ============================================================================

/**
 * Main application layout with sidebar navigation.
 *
 * Structure:
 * ```
 * ┌─────────────────────────────────────────┐
 * │ Sidebar │         Main Content          │
 * │         │                               │
 * │  - Nav  │  ┌──────────────────────────┐ │
 * │  - Queue│  │      Page Content        │ │
 * │  - User │  │       (Outlet)           │ │
 * │         │  └──────────────────────────┘ │
 * └─────────────────────────────────────────┘
 * ```
 *
 * Desktop: Sidebar pushes content, margin-left adjusts
 * Mobile: Sidebar overlays content, backdrop appears, no margin shift
 */
export function MainLayout() {
  const muiTheme = useTheme();
  const queryClient = useQueryClient();
  const sidebarCollapsed = useAppStore(state => state.sidebarCollapsed);
  const toggleSidebar = useAppStore(state => state.toggleSidebar);
  const setSidebarCollapsed = useAppStore(state => state.setSidebarCollapsed);
  const extensionType = useExtensionType();
  useLogExtensionType();
  useWorkTime();
  const workTimeType = useWorkTimeType();

  // Detect mobile view (breakpoint: < 960px)
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  // Socket context for listening to call events
  const { subscribe, isConnected } = useSocketIOContext();

  // Call overlay (top bar + expand modal)
  const { overlayQueue } = useCallOverlayQueue();
  const [overlayExpanded, setOverlayExpanded] = useState(false);
  const [actionsHiddenForQueueId, setActionsHiddenForQueueId] = useState<number | null>(null);
  const [overlayHiddenForQueueId, setOverlayHiddenForQueueId] = useState<number | null>(null);
  const overlayQueueId = overlayQueue?.id;

  // Phone Call Summary Modal state
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [processingModalOpen, setProcessingModalOpen] = useState(false);
  const [currentQueueData, setCurrentQueueData] = useState<QueueModel | null>(null);
  const [deferredSummaries, setDeferredSummaries] = useState<QueueModel[]>([]);

  // Track QueueModel data by call_id for lookup when status/summary_created events arrive
  const callQueueMapRef = useRef<Map<number, QueueModel>>(new Map());
  // Calls waiting for transcription — state so effects re-run when items are added/removed
  const [waitingForTranscript, setWaitingForTranscript] = useState<number[]>([]);
  // Calls whose summary is ready but haven't been shown yet
  const [readyForSummary, setReadyForSummary] = useState<number[]>([]);

  // Call overlay: Answer/End wired to WebRTC (SIP.js) when user is web_rtc
  const answerSipCall = useSipCallStore(s => s.answer);
  const declineSipCall = useSipCallStore(s => s.decline);
  const hangUpSipCall = useSipCallStore(s => s.hangUp);
  const toggleMute = useSipCallStore(s => s.toggleMute);
  const isMuted = useSipCallStore(s => s.isMuted);
  const pendingInvitation = useSipCallStore(s => s.pendingInvitation);
  const activeSession = useSipCallStore(s => s.activeSession);

  // Screen reader announcements: only phase transitions are announced (not mute/hold or re-renders)
  const isOverlayCallActive = overlayQueue?.status === 'active';
  const callPhase: CallPhase = activeSession || isOverlayCallActive ? 'connected' : pendingInvitation ? 'incoming' : overlayQueue?.status === 'ringing' ? 'ringing' : 'idle';
  const prevCallPhaseRef = useRef<CallPhase>('idle');
  const [callAnnouncement, setCallAnnouncement] = useState('');
  useEffect(() => {
    const prevPhase = prevCallPhaseRef.current;
    if (prevPhase === callPhase) return;
    prevCallPhaseRef.current = callPhase;
    if (callPhase !== 'idle') setCallAnnouncement(CALL_ANNOUNCEMENTS[callPhase]);
    else setCallAnnouncement('Call ended');
  }, [callPhase]);

  const isCallOverlayActive = !!pendingInvitation || !!activeSession;
  const isCallStatusActive =
    overlayQueue?.status === 'dialing' ||
    overlayQueue?.status === 'ringing' ||
    overlayQueue?.status === 'active';
  const shouldBlockSummary = isCallOverlayActive || isCallStatusActive;
  const isAnyModalOpen = summaryModalOpen || processingModalOpen;

  const openSummaryModal = useCallback((queueData: QueueModel) => {
    setCurrentQueueData(queueData);
    setSummaryModalOpen(true);
  }, []);

  const openProcessingModal = useCallback((queueData: QueueModel) => {
    setCurrentQueueData(queueData);
    setProcessingModalOpen(true);
  }, []);

  // Socket.IO subscription for v2 call events — purely additive, no blocking checks.
  // Effects below handle showing/hiding modals based on reactive state.
  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe(event => {
      if (event.namespace !== SocketIONamespace.Call) return;

      // created — track QueueModel by call_id
      if (event.event === 'created') {
        queryClient.invalidateQueries({ queryKey: callFollowUpKeys.all });
        queryClient.invalidateQueries({ queryKey: callHistoryKeys.all });
        const queueData = v2CallResponseToQueueModel(event.data as V2CallResponse);
        callQueueMapRef.current.set(queueData.id, queueData);
      }

      // status: ended — mark as waiting for transcription
      if (event.event === 'status') {
        const statusData = event.data as CallStatusEventData;
        if (statusData.status === 'ended') {
          queryClient.invalidateQueries({ queryKey: callFollowUpKeys.all });
          queryClient.invalidateQueries({ queryKey: callHistoryKeys.all });

          // Ensure call is in the map (created may not have arrived)
          if (!callQueueMapRef.current.has(statusData.call_id)) {
            callQueueMapRef.current.set(statusData.call_id, {
              id: statusData.call_id,
              name: '', role: '', relationship: '', status: statusData.status as CallStatus,
              order: 0, callee: '', caller: {}, tags: [], tier: '',
              has_transcript: false, is_fallback: false, has_call_detail: false,
            });
          }

          const queueData = callQueueMapRef.current.get(statusData.call_id)!;
          if (!queueData.has_call_detail) {
            setWaitingForTranscript(prev =>
              prev.includes(statusData.call_id) ? prev : [...prev, statusData.call_id]
            );
          }
        }

        if (statusData.status === 'closed') {
          queryClient.invalidateQueries({ queryKey: callFollowUpKeys.all });
          queryClient.invalidateQueries({ queryKey: callHistoryKeys.all });
        }
      }

      // summary_created — move from waiting to ready
      if (event.event === 'summary_created') {
        const summaryData = event.data as CallSummaryCreatedEventData;
        setWaitingForTranscript(prev => prev.filter(id => id !== summaryData.call_id));
        const queueData = callQueueMapRef.current.get(summaryData.call_id);
        if (queueData && !queueData.has_call_detail) {
          setReadyForSummary(prev =>
            prev.includes(summaryData.call_id) ? prev : [...prev, summaryData.call_id]
          );
        }
      }
    });

    return unsubscribe;
  }, [subscribe, isConnected, queryClient]);

  // Reset action/overlay hiding when the active queue changes
  useEffect(() => {
    if (overlayQueueId == null) {
      setActionsHiddenForQueueId(null);
      setOverlayHiddenForQueueId(null);
      return;
    }
    if (overlayQueueId !== actionsHiddenForQueueId) {
      setActionsHiddenForQueueId(null);
    }
    if (overlayQueueId !== overlayHiddenForQueueId) {
      setOverlayHiddenForQueueId(null);
    }
  }, [overlayQueueId, actionsHiddenForQueueId, overlayHiddenForQueueId]);

  // Effect: when a summary is ready, close processing modal if it's for that call,
  // then show the summary modal (or defer if blocked)
  useEffect(() => {
    if (readyForSummary.length === 0) return;

    const [nextId, ...rest] = readyForSummary;
    const queueData = callQueueMapRef.current.get(nextId);
    if (!queueData) {
      setReadyForSummary(rest);
      return;
    }

    // Close processing modal if it's showing for this call
    if (processingModalOpen && currentQueueData?.id === nextId) {
      setProcessingModalOpen(false);
      setCurrentQueueData(null);
    }

    if (shouldBlockSummary || summaryModalOpen) {
      // Defer: add to deferred list and remove from ready
      setDeferredSummaries(prev =>
        prev.some(item => item.id === nextId) ? prev : [...prev, queueData]
      );
      setReadyForSummary(rest);
    } else if (!processingModalOpen || currentQueueData?.id === nextId) {
      // Show summary modal immediately
      setReadyForSummary(rest);
      openSummaryModal(queueData);
    }
    // If processing modal is open for a different call, wait for it to close
  }, [readyForSummary, shouldBlockSummary, summaryModalOpen, processingModalOpen, currentQueueData, openSummaryModal]);

  // Effect: show processing modal for waiting calls when no modal is open and not blocked
  useEffect(() => {
    if (shouldBlockSummary || isAnyModalOpen || waitingForTranscript.length === 0) return;
    const nextId = waitingForTranscript[0];
    const queueData = callQueueMapRef.current.get(nextId);
    if (queueData) openProcessingModal(queueData);
  }, [shouldBlockSummary, isAnyModalOpen, waitingForTranscript, openProcessingModal]);

  // Drain deferred summaries when conditions clear
  useEffect(() => {
    if (shouldBlockSummary || isAnyModalOpen || deferredSummaries.length === 0) return;
    const [nextQueue, ...rest] = deferredSummaries;
    setDeferredSummaries(rest);
    openSummaryModal(nextQueue);
  }, [deferredSummaries, isAnyModalOpen, openSummaryModal, shouldBlockSummary]);

  // Hide modals when an active call blocks them
  useEffect(() => {
    if (!shouldBlockSummary) return;

    if (summaryModalOpen && currentQueueData) {
      setDeferredSummaries(prev =>
        prev.some(item => item.id === currentQueueData.id) ? prev : [...prev, currentQueueData]
      );
      setSummaryModalOpen(false);
      setCurrentQueueData(null);
    }

    if (processingModalOpen) {
      setProcessingModalOpen(false);
      setCurrentQueueData(null);
    }
  }, [shouldBlockSummary, summaryModalOpen, processingModalOpen, currentQueueData]);

  // Handle summary modal close — clean up tracking
  const handleSummaryModalClose = () => {
    if (currentQueueData?.id != null) {
      callQueueMapRef.current.delete(currentQueueData.id);
    }
    setSummaryModalOpen(false);
    setCurrentQueueData(null);
  };

  // Handle processing modal close — stop waiting, summary_created will still trigger detail form
  const handleProcessingModalClose = () => {
    const callId = currentQueueData?.id;
    if (callId != null) {
      setWaitingForTranscript(prev => prev.filter(id => id !== callId));
    }
    setProcessingModalOpen(false);
    setCurrentQueueData(null);
  };

  // Handle "Proceed to next call" — stop waiting for transcript entirely
  const handleProcessingModalProceed = () => {
    const callId = currentQueueData?.id;
    if (callId != null) {
      setWaitingForTranscript(prev => prev.filter(id => id !== callId));
      setReadyForSummary(prev => prev.filter(id => id !== callId));
      callQueueMapRef.current.delete(callId);
    }
    setProcessingModalOpen(false);
    setCurrentQueueData(null);
  };

  const currentSidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

  // Margin-left only applies on desktop (push mode)
  // Mobile:
  // - Collapsed: reserve slim rail width so content isn't blocked
  // - Expanded: overlay mode, no margin shift
  const marginLeft = isMobile ? (sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : 0) : currentSidebarWidth;

  // Ensure a sensible default on mobile without fighting user interactions:
  // collapse once when entering mobile view (expanded state becomes overlay-only).
  const prevIsMobileRef = useRef<boolean>(isMobile);
  useEffect(() => {
    const wasMobile = prevIsMobileRef.current;
    prevIsMobileRef.current = isMobile;
    if (!wasMobile && isMobile) {
      setSidebarCollapsed(true);
    }
  }, [isMobile, setSidebarCollapsed]);

  // Mobile: enable swipe (left -> right) to open the drawer when collapsed.
  // The swipe starts from the *left edge of the content area* (next to the slim rail),
  // which avoids interfering with taps/scroll inside the sidebar rail.
  const swipeStateRef = useRef<{
    startX: number;
    startY: number;
    tracking: boolean;
  } | null>(null);

  const EDGE_PX = 24; // swipe must start within left edge
  const OPEN_THRESHOLD_PX = 64; // minimum horizontal movement to open
  const MAX_VERTICAL_DRIFT_PX = 32; // cancel if user is scrolling vertically

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobile || !sidebarCollapsed) return;
    const t = e.touches[0];
    if (!t) return;
    const rect = e.currentTarget.getBoundingClientRect();
    if (t.clientX - rect.left > EDGE_PX) return;
    swipeStateRef.current = { startX: t.clientX, startY: t.clientY, tracking: true };
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const state = swipeStateRef.current;
    if (!state?.tracking) return;
    if (!isMobile || !sidebarCollapsed) {
      swipeStateRef.current = null;
      return;
    }

    const t = e.touches[0];
    if (!t) return;
    const dx = t.clientX - state.startX;
    const dy = t.clientY - state.startY;

    // If the gesture is primarily vertical (scroll), cancel tracking.
    if (Math.abs(dy) > MAX_VERTICAL_DRIFT_PX) {
      swipeStateRef.current = null;
      return;
    }

    if (dx >= OPEN_THRESHOLD_PX) {
      swipeStateRef.current = null;
      toggleSidebar();
    }
  };

  const handleTouchEnd = () => {
    swipeStateRef.current = null;
  };

  // Hold/unhold via POST /queue/control?action=hold|unhold (queue module), not SIP hold
  const { isOnHold, toggleHold } = useQueueHoldControl({ enabled: !!activeSession });

  const handleOverlayAnswer = () => {
    if (overlayQueue?.id !== undefined) {
      setActionsHiddenForQueueId(overlayQueue.id);
    }
    answerSipCall();
  };
  const handleOverlayEnd = () => {
    if (overlayQueue?.id !== undefined) {
      setOverlayHiddenForQueueId(overlayQueue.id);
    }
    setOverlayExpanded(false);
    if (pendingInvitation) declineSipCall();
    else hangUpSipCall();
  };

  // Global keyboard accelerators for call controls.
  // Omitting a handler disables its shortcut.
  useTelephonyHotkeys({
    onAnswer: pendingInvitation ? handleOverlayAnswer : undefined,
    onHangUp: pendingInvitation || activeSession ? handleOverlayEnd : undefined,
    onToggleMute: activeSession ? toggleMute : undefined,
    onToggleHold: activeSession ? toggleHold : undefined,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        bgcolor: 'background.default',
        overflow: 'hidden', // prevent body/document scrolling; pages manage scroll inside the layout
      }}
    >
      {/* Sidebar Navigation */}
      <SidebarMain collapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      {/* Backdrop - Mobile Only */}
      {/* Appears when sidebar is open on mobile, clicking closes sidebar */}
      {!sidebarCollapsed && isMobile && (
        <Box
          onClick={toggleSidebar}
          sx={{
            position: 'fixed',
            inset: 0, // top: 0, right: 0, bottom: 0, left: 0
            bgcolor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent black
            zIndex: muiTheme => muiTheme.zIndex.drawer - 1, // Below drawer but above content
            transition: muiTheme.transitions.create('opacity', {
              duration: muiTheme.transitions.duration.standard,
            }),
          }}
        />
      )}

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0, // Allow content to shrink
          minHeight: 0, // Allow children to use overflow without forcing the whole page to scroll
          overflow: 'auto', // Default scroll container for pages that don't implement their own scroll
          ml: `${marginLeft}px`,
          transition: muiTheme =>
            muiTheme.transitions.create('margin-left', {
              duration: muiTheme.transitions.duration.standard,
            }),
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        {/* Call overlay: top-center bar (sticky), gated by feature flag */}
        {ENABLE_CALL_OVERLAY && overlayQueue && overlayQueue.id !== overlayHiddenForQueueId && (
          <>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                height: 0,
                overflow: 'visible',
                pointerEvents: 'none',
                zIndex: t => t.zIndex.drawer - 2,
                display: 'flex',
                justifyContent: 'center',
                pt: 1.5,
                px: 1.5,
              }}
            >
              {!overlayExpanded && (
                <Box sx={{ pointerEvents: 'auto', maxWidth: 'calc(100% - 24px)', width: { xs: '100%', sm: 560 } }}>
                  <CallOverlayBar
                    queue={overlayQueue}
                    onAnswer={handleOverlayAnswer}
                    onEnd={handleOverlayEnd}
                    onExpand={() => setOverlayExpanded(true)}
                    showActions={extensionType === 'web_rtc' && (!!pendingInvitation || !!activeSession) && overlayQueue.id !== actionsHiddenForQueueId}
                    showInCallControls={!!activeSession}
                    isMuted={isMuted}
                    isOnHold={isOnHold}
                    onToggleMute={toggleMute}
                    onToggleHold={toggleHold}
                  />
                </Box>
              )}
            </Box>
            <CallOverlayModal
              open={overlayExpanded}
              queue={overlayQueue}
              onAnswer={handleOverlayAnswer}
              onEnd={handleOverlayEnd}
              onCollapse={() => setOverlayExpanded(false)}
              showActions={extensionType === 'web_rtc' && (!!pendingInvitation || !!activeSession) && overlayQueue.id !== actionsHiddenForQueueId}
              showInCallControls={!!activeSession}
              isMuted={isMuted}
              isOnHold={isOnHold}
              onToggleMute={toggleMute}
              onToggleHold={toggleHold}
            />
          </>
        )}

        <Suspense fallback={<PageLoading />}>
          <Outlet />
        </Suspense>
      </Box>

      {/* Phone Call Summary Modal */}
      <PhoneCallSummaryModal open={summaryModalOpen} onClose={handleSummaryModalClose} queueData={currentQueueData} />

      {/* Processing Transcript Modal */}
      <ProcessingTranscriptModal
        open={processingModalOpen}
        onClose={handleProcessingModalClose}
        onProceed={handleProcessingModalProceed}
        isWorking={workTimeType === 'working'}
      />

      {/* Telephony live region: persistent so screen readers announce call state changes */}
      <Box role="alert" aria-live="assertive" aria-atomic="true" sx={visuallyHiddenSx}>
        {callAnnouncement}
      </Box>

      {/* WebRTC remote audio */}
      <audio id="sip-remote-audio" autoPlay style={{ position: 'fixed', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }} />
    </Box>
  );
}
